import { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { userDetailsSelector } from '../store/account/selector';
import { activeSubscriptionSelector } from '../store/payment/selector';
import { parseISO, isAfter, startOfDay } from "date-fns";

const useSubscriptionMonitor = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userDetails = useSelector(userDetailsSelector);
  const activeSubscription = useSelector(activeSubscriptionSelector);

  const checkSubscriptionExpiry = useCallback(() => {
    // Skip check for invited users
    if (userDetails?.isInvitedUserAccesible || userDetails?.isInvitedBySeat === "ACCEPTED") {
      return;
    }

    // If user has no planId, redirect to subscription
    if (!userDetails?.planId) {
      navigate('/subscription', { replace: true });
      return;
    }

    // Check if subscription has expired
    if (activeSubscription?.planEndDate) {
      if (isAfter(startOfDay(new Date()), startOfDay(new Date(activeSubscription.planEndDate))) ) {
        // Plan has expired, redirect to subscription page
        navigate('/subscription', { replace: true });
        return;
      }
    }
  }, [userDetails, activeSubscription, navigate]);

  useEffect(() => {
    // Initial check
    if (userDetails?._id) {
      checkSubscriptionExpiry();
    }
  }, [userDetails, activeSubscription, checkSubscriptionExpiry]);

  useEffect(() => {
    // Set up periodic checking every 5 minutes
    const intervalId = setInterval(() => {
      if (userDetails?._id) {
        checkSubscriptionExpiry();
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(intervalId);
  }, [userDetails, checkSubscriptionExpiry]);

  return { checkSubscriptionExpiry };
};

export default useSubscriptionMonitor;
